import { Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface MobileNavButtonProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  showOnInbox?: boolean;
}

export const MobileNavButton = ({ isOpen, setIsOpen, showOnInbox = true }: MobileNavButtonProps) => {
  const [position, setPosition] = useState({ x: 16, y: 16 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    
    const newX = e.clientX - startPos.x;
    const newY = e.clientY - startPos.y;
    
    // Constrain to viewport bounds
    const maxX = window.innerWidth - (buttonRef.current?.offsetWidth || 0);
    const maxY = window.innerHeight - (buttonRef.current?.offsetHeight || 0);
    
    const x = Math.max(0, Math.min(newX, maxX));
    const y = Math.max(0, Math.min(newY, maxY));
    
    setPosition({ x, y });
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // Snap to left or right side
    const halfScreen = window.innerWidth / 2;
    const newX = position.x < halfScreen ? 16 : window.innerWidth - (buttonRef.current?.offsetWidth || 0) - 16;
    
    setPosition(prev => ({
      ...prev,
      x: newX
    }));
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  if (!showOnInbox) return null;

  return (
    <button
      ref={buttonRef}
      onMouseDown={handleMouseDown}
      onClick={() => setIsOpen(!isOpen)}
      style={{
        position: 'fixed',
        top: '0.875rem', // Adjusted to be perfectly centered in the top bar (14px)
        left: isOpen ? `${17 * 16}px` : `${position.x}px`,
        cursor: isDragging ? 'grabbing' : 'grab',
        transition: isDragging ? 'none' : 'all 0.3s ease-in-out',
        zIndex: 50,
      }}
      className="lg:hidden p-2 rounded-lg bg-background/95 border border-border"
    >
      {isOpen ? 
        <X className="w-6 h-6 text-foreground" /> : 
        <Menu className="w-6 h-6 text-foreground" />
      }
    </button>
  );
};